const DEFAULT_LOCAL_API_BASE_URL = "http://127.0.0.1:3100";
const API_BASE_STORAGE_KEY = "yige:api-base-url";
const MOCK_ENABLED_QUERY_KEY = "mock";
const API_BASE_QUERY_KEY = "apiBaseUrl";
const API_ORIGIN_QUERY_KEY = "apiOrigin";
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);

const ABSOLUTE_PROTOCOL_REGEXP = /^(?:[a-z][a-z\d+.-]*:)?\/\//i;
const NON_HTTP_PROTOCOL_REGEXP = /^(?:data|blob|mailto|tel):/i;

const normalizeBaseUrl = (value?: string | null) => {
  if (!value) return DEFAULT_LOCAL_API_BASE_URL;
  try {
    return new URL(value, window.location.origin).toString().replace(/\/+$/, "");
  } catch (_error) {
    return DEFAULT_LOCAL_API_BASE_URL;
  }
};

const appendPathToBaseUrl = (baseUrl: string, requestPath: string) => {
  const normalizedBaseUrl = `${baseUrl.replace(/\/+$/, "")}/`;
  const normalizedPath = requestPath.replace(/^\/+/, "");
  return new URL(normalizedPath, normalizedBaseUrl).toString();
};

const readSearchParams = () => {
  try {
    return new URLSearchParams(window.location.search);
  } catch (_error) {
    return new URLSearchParams();
  }
};

const readLocalStorageValue = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch (_error) {
    return null;
  }
};

const isLocalLikeLocation = () => {
  const { hostname, protocol } = window.location;
  return protocol === "file:" || LOCAL_HOSTS.has(hostname);
};

const shouldRewritePathname = (pathname: string) => {
  return pathname.startsWith("/") && !pathname.startsWith("/public/") && !pathname.startsWith("/__mock/");
};

const getRequestedPath = (url: string) => {
  if (NON_HTTP_PROTOCOL_REGEXP.test(url) || url.startsWith("#")) return null;
  if (ABSOLUTE_PROTOCOL_REGEXP.test(url)) {
    const parsedUrl = new URL(url, window.location.origin);
    if (parsedUrl.origin !== window.location.origin) return null;
    if (!shouldRewritePathname(parsedUrl.pathname)) return null;
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  }
  if (!url.startsWith("/")) return null;
  if (!shouldRewritePathname(url)) return null;
  return url;
};

const buildRequestRuntimeConfig = (): YigeRequestRuntimeConfig => {
  const searchParams = readSearchParams();
  const globalConfig = window.YIGE_REQUEST_CONFIG || {};
  const mockQueryValue = searchParams.get(MOCK_ENABLED_QUERY_KEY);
  const explicitEnabled =
    mockQueryValue === "1" || mockQueryValue === "true"
      ? true
      : mockQueryValue === "0" || mockQueryValue === "false"
        ? false
        : typeof globalConfig.enabled === "boolean"
          ? globalConfig.enabled
          : isLocalLikeLocation();

  const apiBaseUrl = normalizeBaseUrl(
    searchParams.get(API_BASE_QUERY_KEY) ||
      searchParams.get(API_ORIGIN_QUERY_KEY) ||
      globalConfig.apiBaseUrl ||
      globalConfig.apiOrigin ||
      readLocalStorageValue(API_BASE_STORAGE_KEY) ||
      DEFAULT_LOCAL_API_BASE_URL,
  );

  const config: YigeRequestRuntimeConfig = {
    enabled: explicitEnabled,
    apiBaseUrl,
    rewriteRelativeUrls: globalConfig.rewriteRelativeUrls ?? true,
    debug: globalConfig.debug ?? false,
  };

  return config;
};

export const refreshRequestRuntimeConfig = (
  overrides?: YigeRequestGlobalConfig,
): YigeRequestRuntimeConfig => {
  if (overrides) {
    window.YIGE_REQUEST_CONFIG = {
      ...(window.YIGE_REQUEST_CONFIG || {}),
      ...overrides,
    };
  }
  const config = buildRequestRuntimeConfig();
  window.__YIGE_REQUEST_RUNTIME__ = config;
  return config;
};

export const getRequestRuntimeConfig = (): YigeRequestRuntimeConfig =>
  window.__YIGE_REQUEST_RUNTIME__ || refreshRequestRuntimeConfig();

export const resolveRequestUrl = (url: string, config = getRequestRuntimeConfig()) => {
  if (!url || !config.enabled || !config.rewriteRelativeUrls) return url;
  const requestPath = getRequestedPath(url);
  if (!requestPath) return url;
  return appendPathToBaseUrl(config.apiBaseUrl, requestPath);
};

const installJQueryAjaxPrefilter = () => {
  const jquery = window.jQuery as (JQueryStatic & { __yigeRequestPrefilterInstalled__?: boolean }) | undefined;
  if (!jquery?.ajaxPrefilter || jquery.__yigeRequestPrefilterInstalled__) return;

  jquery.ajaxPrefilter((options) => {
    if (typeof options.url === "string") {
      options.url = resolveRequestUrl(options.url);
    }
  });

  jquery.__yigeRequestPrefilterInstalled__ = true;
};

const installFetchPatch = () => {
  if (typeof window.fetch !== "function" || window.__YIGE_ORIGINAL_FETCH__) return;

  const originalFetch = window.fetch.bind(window);
  window.__YIGE_ORIGINAL_FETCH__ = originalFetch;

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === "string") {
      return originalFetch(resolveRequestUrl(input), init);
    }

    if (input instanceof URL) {
      return originalFetch(resolveRequestUrl(input.toString()), init);
    }

    if (input instanceof Request) {
      const resolvedUrl = resolveRequestUrl(input.url);
      if (resolvedUrl !== input.url) {
        return originalFetch(new Request(resolvedUrl, input), init);
      }
    }

    return originalFetch(input, init);
  }) as typeof fetch;
};

export const initRequestRuntime = () => {
  const config = refreshRequestRuntimeConfig();
  window.refreshYigeRequestRuntimeConfig = refreshRequestRuntimeConfig;
  installJQueryAjaxPrefilter();

  if (!window.__YIGE_REQUEST_RUNTIME_INITIALIZED__) {
    installFetchPatch();
    window.__YIGE_REQUEST_RUNTIME_INITIALIZED__ = true;
  }

  if (config.debug) {
    console.info("[YIGE request runtime]", config);
  }

  return config;
};
